import { create } from 'zustand'

import {
  DocumentPreferences,
  FilterPreferences,
  InfoPanelLayoutPreferences,
  LayoutPreferences,
  ModuleTypeEnum,
  PreferenceContextEnum,
  PreferenceScopeEnum,
  TablePreferences,
  ThemePreferences,
  UserPreferenceData,
  ViewPreferences,
} from '@scrambl/shared'

import { userPreferencesApi } from '../lib/api/user-preferences.api'

// Define the union type for possible preference data structures
export type ConcretePreferenceDetails =
  | TablePreferences
  | ThemePreferences
  | FilterPreferences
  | LayoutPreferences
  | DocumentPreferences
  | ViewPreferences
  | InfoPanelLayoutPreferences
  | Record<string, unknown> // Fallback for other/generic preferences

export interface UserPreferencesState {
  preferences: UserPreferenceData[]
  isLoading: boolean
  isInitialized: boolean
  error: Error | null
  fetchUserPreferences: () => Promise<void>
  getPreference: (
    context: PreferenceContextEnum,
    moduleType?: ModuleTypeEnum,
    scope?: PreferenceScopeEnum,
  ) => UserPreferenceData | undefined
  savePreference: (
    context: PreferenceContextEnum,
    preferenceDetails: ConcretePreferenceDetails, // Use the new union type
    moduleType?: ModuleTypeEnum,
    scope?: PreferenceScopeEnum,
  ) => Promise<UserPreferenceData | undefined>
  resetPreferenceToDefault: (
    context: PreferenceContextEnum,
    moduleType?: ModuleTypeEnum,
  ) => Promise<void>
  clearPreferences: () => void // Action to clear preferences (e.g., on logout)
}

const DEBUG_MODE =
  process.env.NODE_ENV === 'development' &&
  localStorage.getItem('USER_PREFS_STORE_DEBUG') === 'true'

const debugLog = (...args: unknown[]) => {
  if (DEBUG_MODE) {
  }
}

const matchesModuleType = (
  prefModuleType: ModuleTypeEnum | null,
  moduleType?: ModuleTypeEnum,
) =>
  prefModuleType === moduleType ||
  (prefModuleType === null && moduleType === undefined)

const pickLatestPreference = (prefs: UserPreferenceData[]) => {
  if (prefs.length === 0) return undefined
  const sorted = [...prefs].sort((a, b) => {
    const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
    const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
    if (aTime !== bTime) return bTime - aTime
    const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0
    const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0
    return bCreated - aCreated
  })
  return sorted[0]
}

export const getUserAppPreference = (preferences: UserPreferenceData[]) =>
  pickLatestPreference(
    preferences.filter(
      p =>
        p.context === PreferenceContextEnum.APP &&
        p.scope === PreferenceScopeEnum.USER &&
        matchesModuleType(p.moduleType, undefined),
    ),
  )

export const useUserPreferencesStore = create<UserPreferencesState>(
  (set, get) => {
    // Debounce mechanism to prevent rapid successive calls
    let fetchTimeout: NodeJS.Timeout | null = null
    let isCurrentlyFetching = false

    return {
      preferences: [],
      isLoading: false,
      isInitialized: false,
      error: null,

      fetchUserPreferences: async () => {
        // Prevent multiple simultaneous fetches
        if (isCurrentlyFetching) {
          debugLog('Fetch already in progress, skipping')
          return
        }

        // If already initialized, don't fetch again
        if (get().isInitialized) {
          debugLog('Preferences already initialized, skipping fetch')
          return
        }

        // Clear any pending fetch timeout
        if (fetchTimeout) {
          clearTimeout(fetchTimeout)
          fetchTimeout = null
        }

        debugLog('Fetching user preferences')
        isCurrentlyFetching = true
        set({ isLoading: true, error: null })
        try {
          const response = await userPreferencesApi.getAllUserPreferences()
          const fetchedPreferences = response.data || []
          debugLog('Fetched preferences from API (/all):', fetchedPreferences)
          const dashboardPrefFromAll = fetchedPreferences.find(
            p =>
              p.moduleType === ModuleTypeEnum.TODAY &&
              p.context === PreferenceContextEnum.LAYOUT,
          )
          debugLog(
            'Dashboard preference found in /all response (moduleType: DASHBOARD, context: LAYOUT):',
            dashboardPrefFromAll,
          )

          // Debug: Check for favorites
          const favoritesPref = fetchedPreferences.find(
            p => p.context === PreferenceContextEnum.FAVORITES,
          )
          debugLog(
            'Favorites preference found in /all response:',
            favoritesPref,
          )

          // Debug: Log all contexts
          const contexts = fetchedPreferences.map(p => p.context)
          debugLog('All contexts in preferences:', contexts)

          set({
            preferences: fetchedPreferences,
            isLoading: false,
            isInitialized: true,
          })
        } catch (err: unknown) {
          debugLog('Error fetching preferences:', err)
          set({ error: err as Error, isLoading: false })
        } finally {
          isCurrentlyFetching = false
        }
      },

      getPreference: (
        context: PreferenceContextEnum,
        moduleType?: ModuleTypeEnum,
        scope?: PreferenceScopeEnum,
      ) => {
        const prefs = get().preferences
        debugLog(
          `Getting preference for context: ${context}, moduleType: ${moduleType}`,
        )
        const matches = (p: UserPreferenceData) =>
          p.context === context && matchesModuleType(p.moduleType, moduleType)

        if (scope === PreferenceScopeEnum.USER) {
          const userPref = pickLatestPreference(
            prefs.filter(
              p => matches(p) && p.scope === PreferenceScopeEnum.USER,
            ),
          )
          if (userPref) {
            debugLog('Found user-specific preference:', userPref)
          }
          return userPref
        }

        if (scope === PreferenceScopeEnum.ORGANIZATION) {
          const orgPref = pickLatestPreference(
            prefs.filter(
              p => matches(p) && p.scope === PreferenceScopeEnum.ORGANIZATION,
            ),
          )
          if (orgPref) {
            debugLog('Found organization-specific preference:', orgPref)
          }
          return orgPref
        }

        const userPref = pickLatestPreference(
          prefs.filter(p => matches(p) && p.scope === PreferenceScopeEnum.USER),
        )
        if (userPref) {
          debugLog('Found user-specific preference:', userPref)
          return userPref
        }

        const orgPref = pickLatestPreference(
          prefs.filter(
            p => matches(p) && p.scope === PreferenceScopeEnum.ORGANIZATION,
          ),
        )
        if (orgPref) {
          debugLog('Found organization-specific preference:', orgPref)
          return orgPref
        }

        debugLog('No preference found for context:', context, moduleType)
        return undefined
      },

      savePreference: async (
        context: PreferenceContextEnum,
        newPreferenceDetails: ConcretePreferenceDetails,
        moduleType?: ModuleTypeEnum,
        scope: PreferenceScopeEnum = PreferenceScopeEnum.USER,
      ) => {
        debugLog(
          `Saving preference for context: ${context}, moduleType: ${moduleType}, scope: ${scope}`,
          newPreferenceDetails,
        )
        // Don't set loading state for preference saves to avoid UI flickering
        try {
          // Cast the preference details to Record<string, unknown> to match the API signature
          const savedPref = await userPreferencesApi.saveUserPreferences(
            context,
            newPreferenceDetails as Record<string, unknown>,
            moduleType,
            scope,
          )

          if (savedPref) {
            debugLog(
              'Successfully saved preference, updating store:',
              savedPref,
            )
            set(state => {
              const updatedPreferences = state.preferences.filter(p => {
                if (
                  !(
                    p.context === context &&
                    matchesModuleType(p.moduleType, moduleType) &&
                    p.scope === scope
                  )
                ) {
                  return true
                }
                if (scope === PreferenceScopeEnum.USER) {
                  return p.userId !== savedPref.userId
                }
                return false
              })
              updatedPreferences.push(savedPref)
              debugLog(
                'Local store preferences array AFTER push:',
                updatedPreferences,
              )
              const dashboardPrefAfterSave = updatedPreferences.find(
                p =>
                  p.moduleType === ModuleTypeEnum.TODAY &&
                  p.context === PreferenceContextEnum.LAYOUT,
              )
              debugLog(
                'Dashboard preference in local store AFTER save (moduleType: DASHBOARD, context: LAYOUT):',
                dashboardPrefAfterSave,
              )
              return { preferences: updatedPreferences, error: null } // Only update preferences and clear errors
            })
            return savedPref
          }
          debugLog('Save preference did not return data.')
          return undefined
        } catch (err: unknown) {
          debugLog('Error saving preference:', err)
          set({ error: err as Error })
          throw err
        }
      },

      resetPreferenceToDefault: async (
        context: PreferenceContextEnum,
        moduleType?: ModuleTypeEnum,
      ) => {
        debugLog(
          `Resetting preference to default for context: ${context}, moduleType: ${moduleType}`,
        )
        set({ isLoading: true })
        try {
          await userPreferencesApi.resetUserPreferences(context, moduleType)
          debugLog('Successfully reset preference on backend, updating store.')
          set(state => ({
            preferences: state.preferences.filter(
              p =>
                !(
                  p.context === context &&
                  p.moduleType === moduleType &&
                  p.scope === PreferenceScopeEnum.USER
                ), // only remove user-scoped pref for this context/module
            ),
            isLoading: false,
            error: null, // Clear error on success
          }))
        } catch (err: unknown) {
          debugLog('Error resetting preference:', err)
          set({ error: err as Error, isLoading: false })
          throw err
        }
      },
      clearPreferences: () => {
        debugLog('Clearing all preferences from the store.')
        set({
          preferences: [],
          isLoading: false,
          error: null,
          isInitialized: false,
        })
        isCurrentlyFetching = false // Reset fetching state
        if (fetchTimeout) {
          clearTimeout(fetchTimeout)
          fetchTimeout = null
        }
      },
    }
  },
)
